"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import {
  ArrowRight,
  Camera,
  CheckCircle2,
  MessageCircle,
  Mic,
  Sparkles,
  Target,
} from "lucide-react";
import TCDLogo from "@/components/brand/TCDLogo";
import { TCDIcons } from "@/components/ui/tcd-icons";
import Section from "./ui/Section";
import SectionHeading from "./ui/SectionHeading";

const capabilities = [
  {
    icon: TCDIcons.mastery,
    title: "Understand difficult concepts",
    description: "Ask Maadhav to explain a topic in clearer, more manageable steps.",
  },
  {
    icon: TCDIcons.target,
    title: "Work through problems",
    description: "Get help reasoning through a question instead of stopping at the answer.",
  },
  {
    icon: TCDIcons.journey,
    title: "Practise your understanding",
    description: "Use follow-up prompts to explore examples and test what you have learned.",
  },
  {
    icon: TCDIcons.achievement,
    title: "Learn from mistakes",
    description: "Revisit a confusing step and ask for another explanation or example.",
  },
];

export default function MaadhavExperience() {
  return (
    <Section className="relative overflow-hidden bg-[#071126]">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-[#274472]/35 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-[30rem] w-[30rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:36px_36px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/35 to-transparent" />
      </div>

      <div className="relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55 }}
          className="mx-auto mb-12 max-w-3xl text-center"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-gold/25 bg-brand-gold/[0.07] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Meet your AI learning companion
          </div>
          <div className="mb-5 flex justify-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-3 shadow-[0_0_50px_rgba(230,192,110,0.09)]">
              <TCDLogo size={64} />
            </div>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Meet <span className="text-brand-gold">Maadhav.AI</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
            Learning is more than finding an answer. Ask questions, explore a difficult idea, work through a problem, and keep learning with a companion built into the TCD experience.
          </p>
        </motion.div>

        <div className="grid items-center gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:gap-14">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55 }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {capabilities.map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-5 transition-colors hover:border-brand-gold/30 hover:bg-white/[0.055]">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/[0.08] text-brand-gold">
                    <span className="h-5 w-5">{item.icon}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white sm:text-base">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-400">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3 text-xs font-medium text-slate-300">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2"><Camera className="h-4 w-4 text-brand-gold" /> Ask about an image</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2"><Mic className="h-4 w-4 text-brand-gold" /> Voice input on supported browsers</span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2"><MessageCircle className="h-4 w-4 text-brand-gold" /> Continue past conversations</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2rem] bg-brand-gold/[0.06] blur-2xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#0B1730] shadow-[0_28px_90px_rgba(0,0,0,0.3)]">
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/[0.08] text-brand-gold"><span className="h-5 w-5">{TCDIcons.mastery}</span></div>
                  <div>
                    <p className="text-sm font-bold text-white">Maadhav</p>
                    <p className="mt-0.5 text-xs text-slate-400">Your TCD AI learning companion</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/[0.08] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-300" /> Learning mode</span>
              </div>

              <div className="space-y-5 p-5 sm:p-6">
                <div className="ml-auto max-w-[88%] rounded-2xl rounded-tr-sm border border-brand-gold/20 bg-brand-gold/[0.09] px-4 py-3 text-sm leading-6 text-slate-100">
                  Can you explain a difficult physics concept step by step, with an example?
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-brand-gold"><span className="h-4 w-4">{TCDIcons.mastery}</span></div>
                  <div className="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.035] p-4">
                    <div className="mb-3 flex items-center gap-2 text-xs font-bold text-brand-gold"><Sparkles className="h-3.5 w-3.5" /> Let&apos;s break it down</div>
                    <div className="space-y-2.5 text-sm leading-6 text-slate-300">
                      <p>First, let&apos;s identify the idea behind the concept.</p>
                      <div className="rounded-xl border border-white/10 bg-[#071126] p-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">A useful way to learn</p>
                        <p className="mt-1 text-slate-200">Understand the principle → connect it to an example → try a question yourself.</p>
                      </div>
                      <p className="text-slate-400">You can ask for a simpler explanation, another example, or a practice question.</p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Explain simpler", "Show an example", "Give me practice"].map((label) => <span key={label} className="rounded-full border border-white/10 px-3 py-1.5 text-[11px] text-slate-300">{label}</span>)}
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#071126]/80 p-3.5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-500">What would you like to understand?</p>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-gold text-[#071126]"><ArrowRight className="h-4 w-4" /></div>
                  </div>
                </div>
                <p className="text-center text-[11px] leading-5 text-slate-500">Illustrative preview. Open Maadhav to start your own conversation.</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
          <Link href="/dashboard/maadhav" className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-brand-gold px-6 py-3 text-sm font-bold text-[#071126] shadow-lg shadow-brand-gold/10 transition hover:-translate-y-0.5 hover:bg-[#f0d38d] focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 focus:ring-offset-[#071126]">
            Start learning with Maadhav <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="inline-flex items-center gap-2 text-xs text-slate-400"><CheckCircle2 className="h-4 w-4 text-brand-gold" /> Part of the TCD learning experience</p>
        </div>
      </div>
    </Section>
  );
}
